import { Icon, styled } from '@moleculeone/ui';
import { Fragment } from 'react';
import { Tooltip2 } from '@blueprintjs/popover2';

import { useConfig, useCreditOverview } from '@f/hooks';

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  padding: '$2',
});

const Title = styled('p', {
  fontSize: '$3',
  fontWeight: 'bold',
});

const Info = styled('p', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  fontSize: '$2',
  color: '$slate8',
  maxWidth: '20rem',
});

const TableContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '10rem 5rem 5rem',
  width: '100%',
});

const TableHeader = styled('div', {
  fontWeight: 'bold',
  padding: '1rem',
  textAlign: 'center',
  backgroundColor: '$blackA3',
  color: '$slate8',
});

const borderStyle = '1px solid $slate8';

const ModelCell = styled('div', {
  padding: '1rem',
  borderRight: borderStyle,
  borderTop: borderStyle,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gridRow: 'span 2',
  color: '$slate8',
});

const Cell = styled('div', {
  padding: '1rem',
  textAlign: 'center',
  borderTop: borderStyle,
  color: '$slate8',
  variants: {
    isLast: {
      true: {
        borderLeft: borderStyle,
        color: '$slate4',
        fontWeight: 'bold',
      },
    },
    isOverBalance: {
      true: {
        backgroundColor: '$redA8',
      },
    },
  },
});

const TriggerElementContainer = styled('div', {
  cursor: 'pointer',
});

interface GroupedVariants {
  [model: string]: { length: number; max_targets?: number; credits?: number }[];
}

interface CreditConsumptionInfoProps {
  triggerElement: React.ReactNode;
}

export function CreditConsumptionInfo(props: CreditConsumptionInfoProps) {
  const { triggerElement } = props;
  const creditOverview = useCreditOverview();
  const config = useConfig();

  if (!creditOverview || !config) {
    return null;
  }

  const { balance } = creditOverview;
  const { models } = config.batchSearch;

  const variants = models.map(model => model.variants.map(variant => ({ ...variant, model: model.label }))).flat();

  const groupedVariants: GroupedVariants = variants.reduce((acc, variant) => {
    const { model, length, credits_per_target } = variant;
    const max_targets = balance / BigInt(credits_per_target);

    if (!acc[model]) {
      acc[model] = [];
    }

    acc[model].push({ length, max_targets });
    return acc;
  }, {});

  return (
    <Tooltip2
      content={
        <Container>
          <Title>Search targets eligibility</Title>
          <Info>
            The table below displays the number of targets available for processing within your organization's current
            credit balance.
          </Info>
          <TableContainer>
            <TableHeader>Model</TableHeader>
            <TableHeader>Length</TableHeader>
            <TableHeader>Targets</TableHeader>
            {Object.keys(groupedVariants).map((model, index) => (
              <Fragment key={index}>
                <ModelCell>{model}</ModelCell>
                {groupedVariants[model].map((variant, idx) => (
                  <Fragment key={idx}>
                    <Cell>{variant.length}</Cell>
                    <Cell isLast>{String(variant.max_targets)}</Cell>
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </TableContainer>
        </Container>
      }
    >
      <TriggerElementContainer>{triggerElement}</TriggerElementContainer>
    </Tooltip2>
  );
}

interface CreditCostInfoProps extends CreditConsumptionInfoProps {
  targetsCount: number;
}

export function CreditCostInfo(props: CreditCostInfoProps) {
  const { triggerElement, targetsCount } = props;
  const creditOverview = useCreditOverview();
  const config = useConfig();

  if (!creditOverview || !config || !targetsCount) {
    return null;
  }

  const { balance } = creditOverview;
  const { models } = config.batchSearch;

  const variants = models.map(model => model.variants.map(variant => ({ ...variant, model: model.label }))).flat();

  const groupedVariants: GroupedVariants = variants.reduce((acc, variant) => {
    const { model, length, credits_per_target } = variant;
    const credits = targetsCount * credits_per_target;

    if (!acc[model]) {
      acc[model] = [];
    }

    acc[model].push({ length, credits });
    return acc;
  }, {});

  return (
    <Tooltip2
      content={
        <Container>
          <Title>Cost in credits</Title>
          <Info>
            The table below displays the processing costs in credits for a target based on the selected parameters.
          </Info>
          <TableContainer>
            <TableHeader>Model</TableHeader>
            <TableHeader>Length</TableHeader>
            <TableHeader>Credits</TableHeader>
            {Object.keys(groupedVariants).map((model, index) => (
              <Fragment key={index}>
                <ModelCell>{model}</ModelCell>
                {groupedVariants[model].map((variant, idx) => (
                  <Fragment key={idx}>
                    <Cell>{variant.length}</Cell>
                    <Cell isLast isOverBalance={Number(variant.credits) > balance}>
                      {variant.credits}
                    </Cell>
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </TableContainer>
        </Container>
      }
    >
      <TriggerElementContainer>{triggerElement}</TriggerElementContainer>{' '}
    </Tooltip2>
  );
}
